import { useEffect, useState } from 'react';
import { Badge, Box, Button, Heading, HStack, Icon, Spinner, Stack, Table, Text } from '@chakra-ui/react';
import { LuArchive, LuArchiveRestore, LuExternalLink } from 'react-icons/lu';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  archiveSeller,
  getSeller,
  listSellerContracts,
  unarchiveSeller,
  updateSeller,
  type ContractStatus,
  type SellerContractSummary,
  type SellerDetail,
} from '@/lib/api';
import { toaster } from '@/components/ui/toaster';
import { SellerForm, type SellerFormValues } from './SellerForm';
import { fmtDate, fmtMoney } from '@/utils/format';

const STATUS_LABEL: Record<ContractStatus, string> = {
  draft: 'Borrador',
  active: 'Activo',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

const STATUS_PALETTE: Record<ContractStatus, string> = {
  draft: 'gray',
  active: 'green',
  completed: 'blue',
  cancelled: 'red',
};

export function SellerEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [seller, setSeller] = useState<SellerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deals, setDeals] = useState<SellerContractSummary[] | null>(null);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealsError, setDealsError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setDealsLoading(true);
    getSeller(id)
      .then(setSeller)
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'No se pudo cargar'))
      .finally(() => setLoading(false));
    listSellerContracts(id)
      .then(setDeals)
      .catch((err) => setDealsError(err instanceof Error ? err.message : 'No se pudo cargar contratos'))
      .finally(() => setDealsLoading(false));
  }, [id]);

  async function handleSubmit(values: SellerFormValues) {
    if (!id) return;
    setFormError(null);
    setSubmitting(true);
    try {
      await updateSeller(id, {
        fullName: values.fullName.trim(),
        email: values.email.trim() || null,
        phone: values.phone.trim() || null,
        notes: values.notes.trim() || null,
      });
      toaster.create({ title: 'Vendedor actualizado', type: 'success' });
      navigate('/vendedores');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo guardar';
      setFormError(msg);
      toaster.create({ title: 'No se pudo guardar', description: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleArchiveToggle() {
    if (!seller) return;
    setBusy(true);
    try {
      if (seller.status === 'active') {
        await archiveSeller(seller.id);
        toaster.create({ title: 'Vendedor archivado', type: 'success' });
      } else {
        await unarchiveSeller(seller.id);
        toaster.create({ title: 'Vendedor restaurado', type: 'success' });
      }
      navigate('/vendedores');
    } catch (err) {
      toaster.create({ title: 'Operación fallida', description: err instanceof Error ? err.message : '', type: 'error' });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <HStack gap="3" color="fg.muted">
          <Spinner size="sm" />
          <Text fontSize="sm">Cargando…</Text>
        </HStack>
      </Box>
    );
  }

  if (loadError || !seller) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Stack gap="4" maxW="3xl">
          <Text color="red.600" fontSize="sm">
            {loadError ?? 'Vendedor no encontrado'}
          </Text>
          <Box>
            <Button variant="outline" onClick={() => navigate('/vendedores')}>
              Volver
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  }

  const initial: SellerFormValues = {
    fullName: seller.fullName,
    email: seller.email ?? '',
    phone: seller.phone ?? '',
    notes: seller.notes ?? '',
  };

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="3xl">
        <Stack gap="1">
          <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
            Vendedores
          </Text>
          <HStack gap="3" align="center" wrap="wrap">
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              {seller.fullName}
            </Heading>
            <Badge colorPalette={seller.status === 'active' ? 'green' : 'gray'}>
              {seller.status === 'active' ? 'Activo' : 'Archivado'}
            </Badge>
          </HStack>
        </Stack>

        <SellerForm
          initial={initial}
          submitLabel="Guardar cambios"
          submitting={submitting}
          error={formError}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/vendedores')}
        />

        {/* Contratos (read-only) */}
        <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
          <Heading size="sm" mb="4">
            Contratos
          </Heading>

          {dealsLoading ? (
            <HStack gap="2" color="fg.muted">
              <Spinner size="sm" />
              <Text fontSize="sm">Cargando contratos…</Text>
            </HStack>
          ) : dealsError ? (
            <Text color="red.600" fontSize="sm">
              {dealsError}
            </Text>
          ) : !deals || deals.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">
              Sin contratos
            </Text>
          ) : (
            <>
              {/* Mobile: card stack */}
              <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
                {deals.map((ct) => (
                  <Box key={ct.id} borderWidth="1px" borderColor="border" rounded="md" p="3" bg="bg.subtle">
                    <Stack gap="2">
                      <HStack gap="2" wrap="wrap">
                        <Text fontWeight="700">{ct.contractNumber}</Text>
                        <Badge colorPalette={STATUS_PALETTE[ct.status]}>{STATUS_LABEL[ct.status]}</Badge>
                      </HStack>
                      <HStack gap="4" fontSize="sm" wrap="wrap">
                        <Text>
                          <Text as="span" color="fg.muted">
                            Financiado:{' '}
                          </Text>
                          {fmtMoney(ct.financedAmount)}
                        </Text>
                        <Text>
                          <Text as="span" color="fg.muted">
                            Saldo:{' '}
                          </Text>
                          {fmtMoney(ct.balancePending)}
                        </Text>
                        <Text>
                          <Text as="span" color="fg.muted">
                            Próxima cuota:{' '}
                          </Text>
                          {ct.nextDueDate ? fmtDate(ct.nextDueDate) : '—'}
                        </Text>
                      </HStack>
                      <Box>
                        <Button size="xs" variant="outline" asChild>
                          <RouterLink to={`/contratos/${ct.id}`}>
                            <Icon as={LuExternalLink} /> Ver contrato
                          </RouterLink>
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>

              {/* Desktop: table */}
              <Box
                borderWidth="1px"
                borderColor="border"
                rounded="md"
                overflow="hidden"
                display={{ base: 'none', md: 'block' }}
              >
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Número</Table.ColumnHeader>
                      <Table.ColumnHeader>Estado</Table.ColumnHeader>
                      <Table.ColumnHeader>Financiado</Table.ColumnHeader>
                      <Table.ColumnHeader>Saldo pendiente</Table.ColumnHeader>
                      <Table.ColumnHeader>Próxima cuota</Table.ColumnHeader>
                      <Table.ColumnHeader />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {deals.map((ct) => (
                      <Table.Row key={ct.id}>
                        <Table.Cell fontWeight="600">{ct.contractNumber}</Table.Cell>
                        <Table.Cell>
                          <Badge colorPalette={STATUS_PALETTE[ct.status]}>{STATUS_LABEL[ct.status]}</Badge>
                        </Table.Cell>
                        <Table.Cell>{fmtMoney(ct.financedAmount)}</Table.Cell>
                        <Table.Cell>{fmtMoney(ct.balancePending)}</Table.Cell>
                        <Table.Cell color="fg.muted">
                          {ct.nextDueDate ? fmtDate(ct.nextDueDate) : '—'}
                        </Table.Cell>
                        <Table.Cell>
                          <HStack justify="flex-end">
                            <Button size="xs" variant="outline" asChild>
                              <RouterLink to={`/contratos/${ct.id}`}>
                                <Icon as={LuExternalLink} /> Ver
                              </RouterLink>
                            </Button>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            </>
          )}
        </Box>

        <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p={{ base: '4', md: '6' }}>
          <Heading size="sm" mb="2">
            {seller.status === 'active' ? 'Archivar vendedor' : 'Restaurar vendedor'}
          </Heading>
          <Text color="fg.muted" fontSize="sm" mb="4">
            {seller.status === 'active'
              ? 'El vendedor deja de aparecer en las listas activas y en los selectores. Sus contratos asignados se conservan.'
              : 'El vendedor vuelve a estar disponible en las listas y selectores.'}
          </Text>
          <Button
            variant="outline"
            colorPalette={seller.status === 'active' ? 'gray' : 'green'}
            onClick={handleArchiveToggle}
            loading={busy}
            w={{ base: 'full', md: 'auto' }}
          >
            <Icon as={seller.status === 'active' ? LuArchive : LuArchiveRestore} />
            {seller.status === 'active' ? 'Archivar' : 'Restaurar'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
